import { computed, ref, shallowRef } from 'vue'

import { OrpheusApi, orpheusApi } from '../api/OrpheusApi'
import type { ThreadListDto, ThreadSummaryDto } from '../models/orpheus.dto'
import type { ChatThread, WorkspaceId } from '../models/orpheus.types'
import { mapThreadHistory } from '../service/messageMapper'
import { buildThreadState, mergeThreads, sortThreads } from '../service/threadMapper'

export type ThreadReadApi = Pick<OrpheusApi, 'getThreadHistory' | 'listThreads'>
export type HistoryLoadResult = 'loaded' | 'ready' | 'loading' | 'failed' | 'stale' | 'missing'
export type SelectAllState = 'none' | 'some' | 'all'

export interface BulkRemovalSnapshot {
  removed: ChatThread[]
  openLocalId: string | null
  selectedLocalIds: string[]
  selectionMode: boolean
}
export function createOrpheusThreadState() {
  const threads = ref<ChatThread[]>([])
  const selectedLocalId = ref<string | null>(null)
  const listLoading = ref(false)
  const listLoadingMore = ref(false)
  const nextCursor = ref<string | null>(null)
  const hasMoreThreads = ref(false)
  const selectionMode = ref(false)
  const selectedThreadLocalIds = ref<string[]>([])
  const bulkDeleting = ref(false)
  const threadWorkspaceId = ref<WorkspaceId | null>(null)
  const threadReadAbort = shallowRef<AbortController | null>(null)
  let scopeVersion = 0
  let listRequestVersion = 0

  const selectedThread = computed(
    () => threads.value.find((thread) => thread.localId === selectedLocalId.value) ?? null,
  )
  const orderedThreads = computed(() => sortThreads(threads.value))
  const hasThreads = computed(() => threads.value.length > 0)

  function findOwnedThread(
    workspaceId: WorkspaceId | null,
    localId: string,
  ): ChatThread | undefined {
    if (workspaceId === null) return undefined
    return threads.value.find(
      (thread) => sameWorkspace(thread.workspaceId, workspaceId) && thread.localId === localId,
    )
  }

  function ownsThreadWorkspace(workspaceId: WorkspaceId): boolean {
    return sameWorkspace(threadWorkspaceId.value, workspaceId)
  }

  function claimThreadWorkspace(workspaceId: WorkspaceId): void {
    if (threadWorkspaceId.value === null) {
      threadWorkspaceId.value = workspaceId
      scopeVersion += 1
      return
    }
    if (sameWorkspace(threadWorkspaceId.value, workspaceId)) return
    resetThreadData()
    threadWorkspaceId.value = workspaceId
    scopeVersion += 1
  }

  async function loadThreads(
    workspaceId: WorkspaceId,
    api: ThreadReadApi = orpheusApi,
  ): Promise<boolean> {
    const read = beginRead(workspaceId)
    const requestVersion = ++listRequestVersion
    listLoading.value = true
    try {
      const page = await api.listThreads(workspaceId, null, { signal: read.signal })
      if (!ownsRead(workspaceId, read.version) || requestVersion !== listRequestVersion)
        return false
      applyInitialPage(workspaceId, page)
      return true
    } catch {
      if (ownsRead(workspaceId, read.version) && !read.signal.aborted) {
        threads.value = threads.value.filter(
          (thread) => sameWorkspace(thread.workspaceId, workspaceId) && thread.threadId === null,
        )
        nextCursor.value = null
        hasMoreThreads.value = false
        pruneSelection()
      }
      return false
    } finally {
      if (requestVersion === listRequestVersion) listLoading.value = false
    }
  }

  async function loadMoreThreads(
    workspaceId: WorkspaceId,
    api: ThreadReadApi = orpheusApi,
  ): Promise<boolean> {
    if (
      listLoadingMore.value ||
      !hasMoreThreads.value ||
      !nextCursor.value ||
      !sameWorkspace(threadWorkspaceId.value, workspaceId)
    ) {
      return false
    }
    const before = nextCursor.value
    const read = beginRead(workspaceId)
    const requestVersion = ++listRequestVersion
    listLoadingMore.value = true
    try {
      const page = await api.listThreads(workspaceId, before, { signal: read.signal })
      if (!ownsRead(workspaceId, read.version) || requestVersion !== listRequestVersion)
        return false
      applyAppendedPage(workspaceId, before, page)
      return true
    } catch {
      return false
    } finally {
      if (requestVersion === listRequestVersion) listLoadingMore.value = false
    }
  }

  async function selectThreadWithHistory(
    workspaceId: WorkspaceId,
    localId: string | null,
    api: ThreadReadApi = orpheusApi,
  ): Promise<HistoryLoadResult> {
    if (selectionMode.value) return 'missing'
    if (localId === null) {
      selectedLocalId.value = null
      return 'ready'
    }
    const thread = findOwnedThread(workspaceId, localId)
    if (!thread) return 'missing'
    selectedLocalId.value = localId
    return ensureThreadHistory(workspaceId, localId, api)
  }

  async function ensureThreadHistory(
    workspaceId: WorkspaceId,
    localId: string,
    api: ThreadReadApi = orpheusApi,
  ): Promise<HistoryLoadResult> {
    const thread = findOwnedThread(workspaceId, localId)
    if (!thread?.threadId) return 'missing'
    if (thread.historyLoaded) return 'ready'
    if (thread.historyLoading) return 'loading'

    const threadId = thread.threadId
    const read = beginRead(workspaceId)
    thread.historyLoading = true
    try {
      const history = await api.getThreadHistory(workspaceId, threadId, { signal: read.signal })
      if (!ownsRead(workspaceId, read.version)) return 'stale'
      const current = findOwnedThread(workspaceId, localId)
      if (!current || current.threadId !== threadId) return 'stale'
      current.messages = mapThreadHistory(history, current.localId)
      current.messageCount = current.messages.length
      current.historyLoaded = true
      return 'loaded'
    } catch {
      return read.signal.aborted ? 'stale' : 'failed'
    } finally {
      const current = findOwnedThread(workspaceId, localId)
      if (current?.threadId === threadId) current.historyLoading = false
    }
  }

  function updateThreadIfOwned(
    workspaceId: WorkspaceId,
    localId: string,
    expectedThreadId: string,
    patch: Partial<Pick<ChatThread, 'title' | 'bookmarked'>>,
  ): boolean {
    const thread = findOwnedThread(workspaceId, localId)
    if (!thread || thread.threadId !== expectedThreadId) return false
    Object.assign(thread, patch)
    threads.value = sortThreads(threads.value)
    return true
  }

  function removeThreadIfOwned(
    workspaceId: WorkspaceId,
    localId: string,
    expectedThreadId: string,
  ): boolean {
    const thread = findOwnedThread(workspaceId, localId)
    if (!thread || thread.threadId !== expectedThreadId) return false
    threads.value = threads.value.filter((candidate) => candidate.localId !== localId)
    selectedThreadLocalIds.value = selectedThreadLocalIds.value.filter((id) => id !== localId)
    if (selectedLocalId.value === localId) selectedLocalId.value = null
    return true
  }

  function beginBulkRemoval(localIds: readonly string[]): BulkRemovalSnapshot {
    const ids = new Set(localIds)
    const snapshot: BulkRemovalSnapshot = {
      removed: threads.value.filter((thread) => ids.has(thread.localId)),
      openLocalId: selectedLocalId.value,
      selectedLocalIds: [...selectedThreadLocalIds.value],
      selectionMode: selectionMode.value,
    }
    threads.value = threads.value.filter((thread) => !ids.has(thread.localId))
    if (selectedLocalId.value && ids.has(selectedLocalId.value)) selectedLocalId.value = null
    bulkDeleting.value = true
    return snapshot
  }

  function restoreBulkRemoval(
    snapshot: BulkRemovalSnapshot,
    failedServerIds?: readonly string[],
  ): void {
    const failed = failedServerIds ? new Set(failedServerIds) : null
    const restored = failed
      ? snapshot.removed.filter((thread) => thread.threadId && failed.has(thread.threadId))
      : snapshot.removed
    threads.value = mergeThreads(threads.value, restored)
    const restoredLocalIds = new Set(restored.map((thread) => thread.localId))
    if (snapshot.openLocalId && restoredLocalIds.has(snapshot.openLocalId)) {
      selectedLocalId.value = snapshot.openLocalId
    }
    selectedThreadLocalIds.value = snapshot.selectedLocalIds.filter((id) =>
      restoredLocalIds.has(id),
    )
    selectionMode.value = snapshot.selectionMode
  }

  function finishBulkRemoval(): void {
    bulkDeleting.value = false
    resetSelectionState()
  }

  function toggleSelectionMode(): void {
    selectionMode.value = !selectionMode.value
    selectedThreadLocalIds.value = []
  }

  function startThreadSelection(localId: string): void {
    if (!deletableThread(localId)) return
    selectionMode.value = true
    if (!selectedThreadLocalIds.value.includes(localId)) {
      selectedThreadLocalIds.value = [localId, ...selectedThreadLocalIds.value]
    }
  }

  function toggleThreadSelection(localId: string): void {
    if (!selectionMode.value || !deletableThread(localId)) return
    selectedThreadLocalIds.value = selectedThreadLocalIds.value.includes(localId)
      ? selectedThreadLocalIds.value.filter((id) => id !== localId)
      : [...selectedThreadLocalIds.value, localId]
  }

  function toggleAllSelection(visibleLocalIds: readonly string[]): void {
    if (!selectionMode.value) return
    const eligible = eligibleLocalIds(visibleLocalIds)
    const selected = new Set(selectedThreadLocalIds.value)
    const allSelected = eligible.length > 0 && eligible.every((id) => selected.has(id))
    selectedThreadLocalIds.value = allSelected
      ? selectedThreadLocalIds.value.filter((id) => !eligible.includes(id))
      : [...new Set([...selectedThreadLocalIds.value, ...eligible])]
  }

  function selectAllState(visibleLocalIds: readonly string[]): SelectAllState {
    const eligible = eligibleLocalIds(visibleLocalIds)
    if (eligible.length === 0) return 'none'
    const selectedCount = eligible.filter((id) => selectedThreadLocalIds.value.includes(id)).length
    if (selectedCount === 0) return 'none'
    return selectedCount === eligible.length ? 'all' : 'some'
  }

  function resetSelectionState(): void {
    selectionMode.value = false
    selectedThreadLocalIds.value = []
  }

  function cancelThreadReads(): void {
    threadReadAbort.value?.abort()
    threadReadAbort.value = null
    scopeVersion += 1
    listLoading.value = false
    listLoadingMore.value = false
    for (const thread of threads.value) thread.historyLoading = false
  }

  function resetThreadState(): void {
    cancelThreadReads()
    resetThreadData()
    threadWorkspaceId.value = null
    bulkDeleting.value = false
  }

  function applyInitialPage(workspaceId: WorkspaceId, page: ThreadListDto): void {
    const incoming = page.threads.map((summary) => mapSummary(workspaceId, summary))
    const optimistic = threads.value.filter(
      (thread) => sameWorkspace(thread.workspaceId, workspaceId) && thread.threadId === null,
    )
    threads.value = mergeThreads(optimistic, incoming)
    nextCursor.value = page.next_cursor
    hasMoreThreads.value = Boolean(page.has_more && page.next_cursor)
    pruneSelection()
  }

  function applyAppendedPage(workspaceId: WorkspaceId, before: string, page: ThreadListDto): void {
    const existingIds = new Set(threads.value.map((thread) => thread.threadId).filter(Boolean))
    const incoming = page.threads.map((summary) => mapSummary(workspaceId, summary))
    const hasNewThread = incoming.some(
      (thread) => thread.threadId && !existingIds.has(thread.threadId),
    )
    const cursorAdvanced = Boolean(page.next_cursor && page.next_cursor !== before)
    threads.value = mergeThreads(threads.value, incoming)
    nextCursor.value = hasNewThread && cursorAdvanced ? page.next_cursor : null
    hasMoreThreads.value = Boolean(hasNewThread && cursorAdvanced && page.has_more)
  }
  function mapSummary(workspaceId: WorkspaceId, summary: ThreadSummaryDto): ChatThread {
    const existing = threads.value.find(
      (thread) =>
        sameWorkspace(thread.workspaceId, workspaceId) &&
        (thread.threadId === summary.thread_id || thread.localId === summary.local_id),
    )
    return buildThreadState(summary, workspaceId, existing)
  }
  function beginRead(workspaceId: WorkspaceId): { signal: AbortSignal; version: number } {
    claimThreadWorkspace(workspaceId)
    if (!threadReadAbort.value || threadReadAbort.value.signal.aborted) {
      threadReadAbort.value = new AbortController()
    }
    return { signal: threadReadAbort.value.signal, version: scopeVersion }
  }
  function ownsRead(workspaceId: WorkspaceId, version: number): boolean {
    return version === scopeVersion && sameWorkspace(threadWorkspaceId.value, workspaceId)
  }

  function resetThreadData(): void {
    threadReadAbort.value?.abort()
    threadReadAbort.value = null
    threads.value = []
    selectedLocalId.value = null
    listLoading.value = false
    listLoadingMore.value = false
    nextCursor.value = null
    hasMoreThreads.value = false
    resetSelectionState()
  }

  function pruneSelection(): void {
    const available = new Set(threads.value.map((thread) => thread.localId))
    selectedThreadLocalIds.value = selectedThreadLocalIds.value.filter((id) => available.has(id))
    if (selectedLocalId.value && !available.has(selectedLocalId.value)) selectedLocalId.value = null
  }

  function deletableThread(localId: string): boolean {
    return Boolean(threads.value.find((thread) => thread.localId === localId)?.threadId)
  }

  function eligibleLocalIds(localIds: readonly string[]): string[] {
    return [...new Set(localIds.filter((id) => deletableThread(id)))]
  }

  return {
    threads,
    selectedLocalId,
    selectedThread,
    orderedThreads,
    hasThreads,
    listLoading,
    listLoadingMore,
    nextCursor,
    hasMoreThreads,
    selectionMode,
    selectedThreadLocalIds,
    bulkDeleting,
    threadWorkspaceId,
    threadReadAbort,
    findOwnedThread,
    ownsThreadWorkspace,
    claimThreadWorkspace,
    loadThreads,
    loadMoreThreads,
    selectThreadWithHistory,
    ensureThreadHistory,
    updateThreadIfOwned,
    removeThreadIfOwned,
    beginBulkRemoval,
    restoreBulkRemoval,
    finishBulkRemoval,
    toggleSelectionMode,
    startThreadSelection,
    toggleThreadSelection,
    toggleAllSelection,
    selectAllState,
    resetSelectionState,
    cancelThreadReads,
    resetThreadState,
  }
}

function sameWorkspace(left: WorkspaceId | null, right: WorkspaceId | null): boolean {
  return left !== null && right !== null && String(left) === String(right)
}
